// SPDX-License-Identifier: AGPL-3.0-only

export const TERRAIN_VERTEX_SHADER = `#version 300 es
precision highp float;
precision highp int;

layout(location = 0) in vec3 a_mercator_position;
layout(location = 1) in vec2 a_uv;
layout(location = 2) in float a_meters_to_mercator;

uniform mat4 u_matrix;
uniform vec4 u_center_clip;
uniform vec4 u_center_clip_low;
uniform sampler2D u_state;
uniform vec2 u_resolution;
uniform float u_min_elevation;
uniform float u_water_height_scale;
uniform int u_render_water;

out vec2 v_uv;
out float v_elevation;
out float v_depth;
out vec2 v_velocity;
out vec3 v_world_position;
out float v_meters_to_mercator;

vec4 sampleBilinear(sampler2D tex, vec2 uv) {
  vec2 p = uv * u_resolution - 0.5;
  vec2 cell = floor(p);
  vec2 f = fract(p);
  vec2 a = (cell + vec2(0.5, 0.5)) / u_resolution;
  vec2 b = (cell + vec2(1.5, 0.5)) / u_resolution;
  vec2 c = (cell + vec2(0.5, 1.5)) / u_resolution;
  vec2 d = (cell + vec2(1.5, 1.5)) / u_resolution;
  vec4 v00 = texture(tex, clamp(a, 0.0, 1.0));
  vec4 v10 = texture(tex, clamp(b, 0.0, 1.0));
  vec4 v01 = texture(tex, clamp(c, 0.0, 1.0));
  vec4 v11 = texture(tex, clamp(d, 0.0, 1.0));
  return mix(mix(v00, v10, f.x), mix(v01, v11, f.x), f.y);
}

float sampleWaterGeometry(vec2 uv) {
  vec2 texel = 1.0 / u_resolution;
  float depth = max(sampleBilinear(u_state, uv).r, 0.0) * 0.5;
  depth += max(sampleBilinear(u_state, uv + vec2(texel.x, 0.0)).r, 0.0) * 0.125;
  depth += max(sampleBilinear(u_state, uv - vec2(texel.x, 0.0)).r, 0.0) * 0.125;
  depth += max(sampleBilinear(u_state, uv + vec2(0.0, texel.y)).r, 0.0) * 0.125;
  depth += max(sampleBilinear(u_state, uv - vec2(0.0, texel.y)).r, 0.0) * 0.125;
  return depth;
}

void main() {
  vec4 state = u_render_water == 1 ? sampleBilinear(u_state, a_uv) : vec4(0.0);
  float depth = max(state.r, 0.0);
  float geometryDepth = u_render_water == 1 ? sampleWaterGeometry(a_uv) : 0.0;
  float waterOffset = u_render_water == 1
    ? max(geometryDepth, 0.0) * a_meters_to_mercator * u_water_height_scale
    : 0.0;
  vec3 worldPosition = vec3(
    a_mercator_position.xy,
    a_mercator_position.z + waterOffset
  );
  gl_Position = u_matrix * vec4(worldPosition, 0.0) + u_center_clip + u_center_clip_low;
  v_uv = a_uv;
  v_elevation = u_min_elevation +
    a_mercator_position.z / max(a_meters_to_mercator, 1.0e-12);
  v_depth = depth;
  v_velocity = state.gb;
  v_world_position = worldPosition;
  v_meters_to_mercator = a_meters_to_mercator;
}
`;

export const TERRAIN_FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp int;

uniform sampler2D u_state;
uniform sampler2D u_terrain;
uniform sampler2D u_flux;
uniform vec2 u_resolution;
uniform vec2 u_cell_size;
uniform vec3 u_camera_position;
uniform float u_min_elevation;
uniform float u_max_elevation;
uniform int u_render_water;

in vec2 v_uv;
in float v_elevation;
in float v_depth;
in vec2 v_velocity;
in vec3 v_world_position;
in float v_meters_to_mercator;
out vec4 out_color;

vec4 sampleBilinear(sampler2D tex, vec2 uv) {
  vec2 p = uv * u_resolution - 0.5;
  vec2 cell = floor(p);
  vec2 f = fract(p);
  vec2 a = (cell + vec2(0.5, 0.5)) / u_resolution;
  vec2 b = (cell + vec2(1.5, 0.5)) / u_resolution;
  vec2 c = (cell + vec2(0.5, 1.5)) / u_resolution;
  vec2 d = (cell + vec2(1.5, 1.5)) / u_resolution;
  vec4 v00 = texture(tex, clamp(a, 0.0, 1.0));
  vec4 v10 = texture(tex, clamp(b, 0.0, 1.0));
  vec4 v01 = texture(tex, clamp(c, 0.0, 1.0));
  vec4 v11 = texture(tex, clamp(d, 0.0, 1.0));
  return mix(mix(v00, v10, f.x), mix(v01, v11, f.x), f.y);
}

float depthAtCell(vec2 cell) {
  return max(texture(u_state, clamp((cell + 0.5) / u_resolution, 0.0, 1.0)).r, 0.0);
}

float catmullRom(float t, float a, float b, float c, float d) {
  float t2 = t * t;
  float t3 = t2 * t;
  return 0.5 * ((2.0 * b) + (-a + c) * t +
    (2.0 * a - 5.0 * b + 4.0 * c - d) * t2 +
    (-a + 3.0 * b - 3.0 * c + d) * t3);
}

float sampleBicubicDepth(vec2 uv) {
  vec2 p = uv * u_resolution - 0.5;
  vec2 cell = floor(p);
  vec2 f = fract(p);
  float rows[4];
  for (int row = 0; row < 4; row++) {
    float y = float(row) - 1.0;
    rows[row] = catmullRom(
      f.x,
      depthAtCell(cell + vec2(-1.0, y)),
      depthAtCell(cell + vec2(0.0, y)),
      depthAtCell(cell + vec2(1.0, y)),
      depthAtCell(cell + vec2(2.0, y))
    );
  }
  return max(catmullRom(f.y, rows[0], rows[1], rows[2], rows[3]), 0.0);
}

float sampleDepthBlur(vec2 uv) {
  vec2 texel = 1.0 / u_resolution;
  float sum = 0.0;
  float weightSum = 0.0;
  for (int y = -2; y <= 2; y++) {
    for (int x = -2; x <= 2; x++) {
      vec2 offset = vec2(float(x), float(y));
      float weight = exp(-dot(offset, offset) / 2.88);
      sum += max(texture(u_state, clamp(uv + offset * texel * 2.0, 0.0, 1.0)).r, 0.0) * weight;
      weightSum += weight;
    }
  }
  return sum / weightSum;
}

float surfaceHeight(vec2 uv) {
  return sampleBilinear(u_terrain, uv).r + sampleBilinear(u_state, uv).r;
}

vec3 skyReflection(vec3 reflected, vec3 lightDirection) {
  float height = clamp(reflected.z * 0.5 + 0.5, 0.0, 1.0);
  vec3 horizon = vec3(0.72, 0.83, 0.92);
  vec3 zenith = vec3(0.08, 0.24, 0.52);
  vec3 sky = mix(horizon, zenith, pow(height, 1.35));
  float sun = pow(max(dot(normalize(reflected), normalize(lightDirection)), 0.0), 96.0);
  return sky + vec3(1.0, 0.86, 0.62) * sun * 0.45;
}

vec3 terrainRamp(float t) {
  vec3 valley = vec3(0.18, 0.35, 0.24);
  vec3 middle = vec3(0.48, 0.50, 0.31);
  vec3 summit = vec3(0.78, 0.78, 0.72);
  return t < 0.55 ? mix(valley, middle, t / 0.55) : mix(middle, summit, (t - 0.55) / 0.45);
}

void main() {
  if (u_render_water == 0) {
    float range = max(u_max_elevation - u_min_elevation, 1.0);
    float elevationMix = clamp((v_elevation - u_min_elevation) / range, 0.0, 1.0);
    vec3 normal = normalize(cross(
      dFdx(vec3(gl_FragCoord.xy, v_elevation)),
      dFdy(vec3(gl_FragCoord.xy, v_elevation))
    ));
    float light = 0.72 + 0.28 * abs(normal.z);
    out_color = vec4(terrainRamp(elevationMix) * light, 0.82);
    return;
  }

  vec2 texel = 1.0 / u_resolution;
  vec2 uv = clamp(v_uv, texel * 1.5, 1.0 - texel * 1.5);
  float depth = sampleBicubicDepth(v_uv);
  float edgeDepth = sampleDepthBlur(v_uv);

  float surfaceRight = surfaceHeight(uv + vec2(texel.x * 1.5, 0.0));
  float surfaceLeft = surfaceHeight(uv - vec2(texel.x * 1.5, 0.0));
  float surfaceDown = surfaceHeight(uv + vec2(0.0, texel.y * 1.5));
  float surfaceUp = surfaceHeight(uv - vec2(0.0, texel.y * 1.5));
  vec3 normal = normalize(vec3(
    (surfaceLeft - surfaceRight) / max(2.0 * u_cell_size.x, 0.0001),
    (surfaceUp - surfaceDown) / max(2.0 * u_cell_size.y, 0.0001),
    1.0
  ));
  if (normal.z < 0.0) normal = -normal;

  vec2 velocity = vec2(v_velocity.x, -v_velocity.y);
  vec2 velocityRight = sampleBilinear(u_state, uv + vec2(texel.x, 0.0)).gb * vec2(1.0, -1.0);
  vec2 velocityLeft = sampleBilinear(u_state, uv - vec2(texel.x, 0.0)).gb * vec2(1.0, -1.0);
  vec2 velocityDown = sampleBilinear(u_state, uv + vec2(0.0, texel.y)).gb * vec2(1.0, -1.0);
  vec2 velocityUp = sampleBilinear(u_state, uv - vec2(0.0, texel.y)).gb * vec2(1.0, -1.0);
  vec4 flux = sampleBilinear(u_flux, v_uv);
  vec4 fluxRight = sampleBilinear(u_flux, uv + vec2(texel.x, 0.0));
  vec4 fluxLeft = sampleBilinear(u_flux, uv - vec2(texel.x, 0.0));
  vec4 fluxDown = sampleBilinear(u_flux, uv + vec2(0.0, texel.y));
  vec4 fluxUp = sampleBilinear(u_flux, uv - vec2(0.0, texel.y));
  vec2 fluxVector = vec2(flux.r - flux.b, flux.a - flux.g);
  vec2 fluxVectorRight = vec2(fluxRight.r - fluxRight.b, fluxRight.a - fluxRight.g);
  vec2 fluxVectorLeft = vec2(fluxLeft.r - fluxLeft.b, fluxLeft.a - fluxLeft.g);
  vec2 fluxVectorDown = vec2(fluxDown.r - fluxDown.b, fluxDown.a - fluxDown.g);
  vec2 fluxVectorUp = vec2(fluxUp.r - fluxUp.b, fluxUp.a - fluxUp.g);

  vec2 velocityGradientX = (velocityRight - velocityLeft) / max(u_cell_size.x, 0.0001);
  vec2 velocityGradientY = (velocityDown - velocityUp) / max(u_cell_size.y, 0.0001);
  vec2 fluxGradientX = (fluxVectorRight - fluxVectorLeft) / max(u_cell_size.x, 0.0001);
  vec2 fluxGradientY = (fluxVectorDown - fluxVectorUp) / max(u_cell_size.y, 0.0001);
  float velocityMagnitude = length(velocity);
  float fluxMagnitude = length(fluxVector);
  float flowActivity = velocityMagnitude + fluxMagnitude * 0.0025;
  vec2 flowDirection = velocityMagnitude > 0.0001
    ? normalize(velocity)
    : (fluxMagnitude > 0.0001 ? normalize(fluxVector) : vec2(1.0, 0.0));
  float divergence = velocityGradientX.x + velocityGradientY.y;
  float curl = velocityGradientY.x - velocityGradientX.y;
  vec2 shear = vec2(
    velocityGradientX.x - velocityGradientY.y,
    velocityGradientX.y + velocityGradientY.x
  );
  vec2 fluxShear = vec2(
    fluxGradientX.x - fluxGradientY.y,
    fluxGradientX.y + fluxGradientY.x
  );
  vec2 waveGradient = flowDirection * divergence * 0.08 +
    vec2(-flowDirection.y, flowDirection.x) * curl * 0.07 +
    shear * 0.045 + fluxShear * 0.0015;
  float activityMask = smoothstep(0.06, 0.30, flowActivity);
  activityMask = activityMask * activityMask * activityMask;
  float waveAmplitude = smoothstep(0.08, 1.6, depth) * activityMask *
    min(0.22, velocityMagnitude * 0.07 + fluxMagnitude * 0.0008);
  normal = normalize(vec3(normal.xy + waveGradient * waveAmplitude, max(normal.z, 0.22)));

  float clampedDepth = clamp(depth, 0.0, 20.0);
  vec3 shallowColor = vec3(0.00, 0.78, 1.00);
  vec3 middleColor = vec3(0.02, 0.43, 0.95);
  vec3 deepColor = vec3(0.02, 0.16, 0.70);
  vec3 waterColor = mix(shallowColor, middleColor, smoothstep(0.0, 0.6, clampedDepth));
  waterColor = mix(waterColor, deepColor, smoothstep(0.6, 4.0, clampedDepth));

  vec3 lightDirection = normalize(vec3(-0.20, 0.15, 1.80));
  vec3 viewDirection = normalize(
    (u_camera_position - v_world_position) / max(v_meters_to_mercator, 1.0e-12)
  );
  float diffuse = max(dot(normal, lightDirection), 0.0) * 0.5 + 0.5;
  vec3 bodyColor = waterColor * diffuse;
  vec3 tangent = normalize(vec3(flowDirection, 0.0));
  vec3 bitangent = normalize(cross(normal, tangent));
  tangent = normalize(cross(bitangent, normal));
  vec3 halfway = normalize(lightDirection + viewDirection);
  float tangentHalfway = dot(tangent, halfway);
  float bitangentHalfway = dot(bitangent, halfway);
  float normalHalfway = max(dot(normal, halfway), 0.0001);
  float anisotropy = smoothstep(0.08, 0.28, velocityMagnitude) * activityMask * 0.45;
  float specular = exp(-(
    mix(90.0, 54.0, anisotropy) * tangentHalfway * tangentHalfway +
    mix(90.0, 28.0, anisotropy) * bitangentHalfway * bitangentHalfway
  ) / (1.0 + 0.25 * normalHalfway));
  specular *= pow(normalHalfway, mix(18.0, 10.0, anisotropy));
  specular *= mix(0.75, 1.0, activityMask);
  vec3 reflected = reflect(-viewDirection, normal);
  vec3 reflectionColor = skyReflection(reflected, lightDirection);
  float fresnel = 0.02 + 0.98 * pow(1.0 - max(dot(normal, viewDirection), 0.0), 5.0);
  float reflectionMix = clamp(
    fresnel * (0.46 + 0.24 * smoothstep(0.12, 3.0, clampedDepth)),
    0.0,
    0.88
  );
  waterColor = mix(bodyColor, reflectionColor, reflectionMix);
  waterColor += vec3(1.0, 0.96, 0.90) * specular *
    (0.08 + 0.12 * fresnel + anisotropy * 0.08);
  float rim = pow(1.0 - max(dot(normal, viewDirection), 0.0), 3.0);
  waterColor += vec3(0.20, 0.35, 0.45) * rim * 0.08;

  float alpha = min(1.0 - exp(-clampedDepth * 4.2), 0.96);
  alpha *= smoothstep(0.0, 0.015, edgeDepth);
  if (alpha < 0.0005) discard;
  out_color = vec4(waterColor, alpha);
}
`;
